import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useEnrollToCareerCenterMutation,
  useSaveCareerMemberMutation,
  CareerMemberInput,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { careerErrorMessage } from './careerModel';

/** The three counters `enrollCareerMembers` reports back. */
export interface IEnrollResult {
  created: number;
  reactivated: number;
  skipped: number;
}

interface ICareerMemberMutationsHook {
  enroll: (params: {
    studentIds?: string[];
    groupId?: string;
    directionId?: string;
  }) => Promise<IEnrollResult | null>;
  updateMember: (id: string, input: CareerMemberInput) => Promise<boolean>;
  pending: boolean;
}

/**
 * Spec 0018 — member-side mutations for the Участники tab.
 *
 * Failures surface the mapped `customErrors` code as an error toast. The
 * enrolment success toast is left to the caller: only it knows how to phrase
 * the created/reactivated/skipped breakdown it just received.
 */
export const useCareerMemberMutations = (
  onChanged?: () => void,
): ICareerMemberMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [enrollRun] = useEnrollToCareerCenterMutation();
  const [updateRun] = useSaveCareerMemberMutation();
  const [pending, setPending] = useState(false);

  const toastError = useCallback(
    (title: string, text?: string) => {
      addToast({
        type: GlobalToastTypeNames.CareerCenter,
        data: { type: ToastTypes.error, title, text },
      });
    },
    [addToast],
  );

  const enroll: ICareerMemberMutationsHook['enroll'] = useCallback(
    async ({ studentIds, groupId, directionId }) => {
      setPending(true);
      try {
        const result = await enrollRun({
          variables: {
            // Send `undefined`, never an empty array — an empty `studentIds`
            // would otherwise read as "enrol nobody" alongside a real groupId.
            studentIds: studentIds?.length ? studentIds : undefined,
            groupId: groupId || undefined,
            directionId: directionId || undefined,
          },
        });
        const payload = result.data?.enrollCareerMembers;
        if (payload?.success) {
          onChanged?.();
          return {
            created: payload.created ?? 0,
            reactivated: payload.reactivated ?? 0,
            skipped: payload.skipped ?? 0,
          };
        }
        toastError(
          t('pages.careerCenter.members.toastEnrollError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.careerCenter.members.toastEnrollError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setPending(false);
      }
    },
    [enrollRun, onChanged, toastError, t],
  );

  const updateMember: ICareerMemberMutationsHook['updateMember'] = useCallback(
    async (id, input) => {
      setPending(true);
      try {
        const result = await updateRun({ variables: { id, input } });
        const payload = result.data?.updateCareerMember;
        if (payload?.success) {
          onChanged?.();
          return true;
        }
        toastError(
          t('pages.careerCenter.members.toastSaveError'),
          careerErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.careerCenter.members.toastSaveError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [updateRun, onChanged, toastError, t],
  );

  return { enroll, updateMember, pending };
};
